// A8 — Strike, ban va apellyatsiya. Module-local data layer (does NOT touch
// `lib/adapters.ts` / `i18n/*` — HOT). Two lanes:
//   • REAL — banned/strikes/appeals endpoints already ship (imported from
//     `@/lib/adapters` in the screens): /admin/banned, /admin/strikes,
//     /admin/appeals, ban/unban/strike/decide.
//   • greenfield — restriction rules / templates / block-lists / reregistration
//     endpoints are not built yet → `safeList` fail-soft to empty, mutations POST
//     when they ship (same pattern as `developers/devApi.ts`).
// Registration = ./REGISTER.md (imports/routes/screenStatus/nav applied by shell owner).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'
import { fetchAllStrikes, type AdminAppeal, type AdminBannedUser, type AdminStrike } from '@/lib/adapters'

function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/* ═══ A8.04 — Avtomatik cheklov qoidalari (LIVE /admin/ban-rules) ═══
   Trigger → threshold → action. CRUD: inline add-form + delete row-action. */
export interface AutoRuleRow {
  id: number
  trigger: string
  threshold: number
  action: 'warn' | 'strike' | 'mute' | 'ban'
  duration_days: number
  enabled: boolean
  created_at?: string
}
export function fetchAutoRules(params: FetchParams): Promise<FetchResult<AutoRuleRow>> {
  return safeList<AutoRuleRow>('/admin/ban-rules', 'rules', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.trigger} ${r.action}`),
  )
}
export function createAutoRule(body: {
  trigger: string
  threshold: number
  action: string
  duration_days: number
  enabled: boolean
}): Promise<{ ok: boolean; id?: number }> {
  return api.post<{ ok: boolean; id?: number }>('/admin/ban-rules', body)
}
export function deleteAutoRule(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/ban-rules/${id}`)
}

/* ═══ A8.09 — Cheklov shablonlari (LIVE /admin/ban-templates) ═══════ */
export interface TemplateRow {
  id: number
  name: string
  action: 'warn' | 'strike' | 'mute' | 'ban'
  severity: number
  duration_days: number
  reason_text: string
  created_at?: string
}
export function fetchTemplates(params: FetchParams): Promise<FetchResult<TemplateRow>> {
  return safeList<TemplateRow>('/admin/ban-templates', 'templates', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.name} ${r.reason_text}`),
  )
}
export function createTemplate(body: {
  name: string
  action: string
  severity: number
  duration_days: number
  reason_text: string
}): Promise<{ ok: boolean; id?: number }> {
  return api.post<{ ok: boolean; id?: number }>('/admin/ban-templates', body)
}
export function deleteTemplate(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/ban-templates/${id}`)
}

/* ═══ A8.15 — Ban sabablari katalogi (LIVE /admin/ban-reasons) ═════ */
export interface ReasonRow {
  id: number
  code: string
  category: string
  label: string
  default_action: 'warn' | 'strike' | 'mute' | 'ban'
  requires_evidence: boolean
  created_at?: string
}
export function fetchBanReasons(params: FetchParams): Promise<FetchResult<ReasonRow>> {
  return safeList<ReasonRow>('/admin/ban-reasons', 'reasons', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.code} ${r.label} ${r.category}`),
  )
}
export function createBanReason(body: {
  code: string
  category: string
  label: string
  default_action: string
  requires_evidence: boolean
}): Promise<{ ok: boolean; id?: number }> {
  return api.post<{ ok: boolean; id?: number }>('/admin/ban-reasons', body)
}
export function deleteBanReason(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/ban-reasons/${id}`)
}

/* ═══ A8.16 — IP/qurilma bloklari (LIVE /admin/ip-blocks) ══════════ */
export interface DeviceBlockRow {
  id: number
  kind: 'ip' | 'device' | 'subnet'
  value: string
  reason: string
  hits: number
  created_at: string
}
export function fetchDeviceBlocks(params: FetchParams): Promise<FetchResult<DeviceBlockRow>> {
  return safeList<DeviceBlockRow>('/admin/ip-blocks', 'blocks', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.value} ${r.reason}`),
  )
}
export function createDeviceBlock(body: {
  kind: string
  value: string
  reason: string
}): Promise<{ ok: boolean; id?: number }> {
  return api.post<{ ok: boolean; id?: number }>('/admin/ip-blocks', body)
}
/** DELETE /admin/ip-blocks/:id — blokni olib tashlash. */
export function removeDeviceBlock(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/ip-blocks/${id}`)
}

/* ═══ A8.17 — Telefon raqam bloklari (LIVE /admin/phone-blocks) ════ */
export interface PhoneBlockRow {
  id: number
  kind: 'number' | 'prefix'
  value: string
  reason: string
  source: 'manual' | 'auto' | 'fraud'
  created_at: string
}
export function fetchPhoneBlocks(params: FetchParams): Promise<FetchResult<PhoneBlockRow>> {
  return safeList<PhoneBlockRow>('/admin/phone-blocks', 'blocks', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.value} ${r.reason}`),
  )
}
export function createPhoneBlock(body: {
  kind: string
  value: string
  reason: string
  source: string
}): Promise<{ ok: boolean; id?: number }> {
  return api.post<{ ok: boolean; id?: number }>('/admin/phone-blocks', body)
}
/** DELETE /admin/phone-blocks/:id — blokni olib tashlash. */
export function removePhoneBlock(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/phone-blocks/${id}`)
}

/* ═══ A8.18 — Qayta ro'yxatdan o'tishni oldini olish ══════════════
   «Bloklangan identifikatorlar» = IP/qurilma bloklari (A8.16) + telefon
   bloklari (A8.17) birlashtirilgan REAL ko'rinishi. Har qatorni o'z jadvalidan
   o'chirish mumkin (qaytadan ro'yxatdan o'tish to'sig'ini olib tashlaydi). */
export interface ReregRuleRow {
  /** Kompozit: `ip:<id>` yoki `ph:<id>` (ikki jadval birlashtirilgani uchun). */
  id: string
  origin: 'ip' | 'phone'
  ref_id: number
  signal: 'ip' | 'device' | 'subnet' | 'phone' | 'prefix'
  match: string
  reason: string
  hits: number
  created_at: string
}
export async function fetchReregBarriers(params: FetchParams): Promise<FetchResult<ReregRuleRow>> {
  const [ipBlocks, phoneBlocks] = await Promise.all([
    safeList<DeviceBlockRow>('/admin/ip-blocks', 'blocks', params.signal),
    safeList<PhoneBlockRow>('/admin/phone-blocks', 'blocks', params.signal),
  ])
  const merged: ReregRuleRow[] = [
    ...ipBlocks.map((b) => ({
      id: `ip:${b.id}`,
      origin: 'ip' as const,
      ref_id: b.id,
      signal: b.kind,
      match: b.value,
      reason: b.reason,
      hits: b.hits ?? 0,
      created_at: b.created_at,
    })),
    ...phoneBlocks.map((b) => ({
      id: `ph:${b.id}`,
      origin: 'phone' as const,
      ref_id: b.id,
      signal: b.kind === 'prefix' ? ('prefix' as const) : ('phone' as const),
      match: b.value,
      reason: b.reason,
      hits: 0,
      created_at: b.created_at,
    })),
  ]
  return toResult(merged, params, (r) => `${r.signal} ${r.match} ${r.reason}`)
}
/** Bloklangan identifikatorni o'z manba-jadvalidan o'chirish. */
export function removeReregBarrier(row: ReregRuleRow): Promise<{ ok: boolean }> {
  return row.origin === 'ip'
    ? api.del<{ ok: boolean }>(`/admin/ip-blocks/${row.ref_id}`)
    : api.del<{ ok: boolean }>(`/admin/phone-blocks/${row.ref_id}`)
}

/* ═══ A8.07 — Muddatni o'zgartirish (REAL data, greenfield mutation) ═
   Aktiv strayklar `/admin/strikes` (real) dan; muddat-o'zgartirish
   endpoint hali yo'q → POST fail-soft (ishga tushganда ulanadi). */
export function fetchActiveStrikes(params: FetchParams): Promise<FetchResult<AdminStrike>> {
  return fetchAllStrikes(params.signal).then((rows) =>
    toResult(
      rows.filter((s) => s.active),
      params,
      (r) => `${r.username ?? r.user_id ?? ''} ${r.reason}`,
    ),
  )
}
export function changeStrikeDuration(id: number, days: number): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/strikes/${id}/duration`, { expires_days: days })
}

/* ═══ A8.12 / A8.14 — apellyatsiya-hosila (REAL /admin/appeals) ══ */
async function appealsRaw(signal: AbortSignal): Promise<AdminAppeal[]> {
  try {
    const data = await api.get<{ appeals: AdminAppeal[] }>('/admin/appeals', {
      query: { limit: 300 },
      signal,
    })
    return data.appeals ?? []
  } catch {
    return []
  }
}
/** A8.12 — hal qilingan apellyatsiyalar (qaror jurnali): status !== pending. */
export function fetchDecidedAppeals(params: FetchParams): Promise<FetchResult<AdminAppeal>> {
  return appealsRaw(params.signal).then((rows) =>
    toResult(
      rows.filter((a) => a.status !== 'pending'),
      params,
      (r) => `${r.username ?? r.user_id} ${r.reason} ${r.decision_note ?? ''}`,
    ),
  )
}
/** A8.14 — noto'g'ri ban tahlili: bekor qilingan (granted) ban-apellyatsiyalari. */
export function fetchWrongfulBans(params: FetchParams): Promise<FetchResult<AdminAppeal>> {
  return appealsRaw(params.signal).then((rows) =>
    toResult(
      rows.filter((a) => a.kind === 'ban' && a.status === 'granted'),
      params,
      (r) => `${r.username ?? r.user_id} ${r.reason}`,
    ),
  )
}

/* ═══ A8.20 — Adolat hisoboti (REAL appeals + banned agregati) ══ */
export interface FairnessData {
  bannedActive: number
  appealsTotal: number
  appealsDecided: number
  granted: number
  rejected: number
  pending: number
  banGranted: number
  strikeGranted: number
}
export async function fetchFairness(signal: AbortSignal): Promise<FairnessData> {
  const [appeals, banned] = await Promise.all([
    appealsRaw(signal),
    (async () => {
      try {
        const d = await api.get<{ banned: AdminBannedUser[] }>('/admin/banned', {
          query: { limit: 300 },
          signal,
        })
        return d.banned ?? []
      } catch {
        return [] as AdminBannedUser[]
      }
    })(),
  ])
  const granted = appeals.filter((a) => a.status === 'granted')
  const rejected = appeals.filter((a) => a.status === 'rejected')
  const pending = appeals.filter((a) => a.status === 'pending')
  return {
    bannedActive: banned.length,
    appealsTotal: appeals.length,
    appealsDecided: granted.length + rejected.length,
    granted: granted.length,
    rejected: rejected.length,
    pending: pending.length,
    banGranted: granted.filter((a) => a.kind === 'ban').length,
    strikeGranted: granted.filter((a) => a.kind === 'strike').length,
  }
}

/* ═══ Module-local RU strings (i18n HOT → keyга ko'chiriladi) ═══ */
export const BAN_STR = {
  // common
  action: 'Действие',
  status: 'Статус',
  reason: 'Причина',
  duration: 'Срок',
  days: 'дн.',
  created: 'Создан',
  enabled: 'Активно',
  save: 'Сохранить',
  cancel: 'Отмена',
  remove: 'Удалить',
  removed: 'Удалено',
  add: 'Добавить',
  addOk: 'Добавлено',
  failed: 'Не удалось выполнить',
  actWarn: 'Предупреждение',
  actStrike: 'Страйк',
  actMute: 'Мут',
  actBan: 'Бан',
  // A8.04 auto rules
  autoRules: 'Правила авто-ограничений',
  autoRulesNote: 'Триггер → порог → действие. Автоматическая эскалация без ручного вмешательства.',
  trigger: 'Триггер',
  threshold: 'Порог',
  // A8.05 manual restriction
  manual: 'Ручное ограничение',
  manualNote: 'Выдать страйк или бан пользователю вручную. Причина обязательна (аудит).',
  userId: 'ID пользователя',
  restrictionType: 'Тип ограничения',
  severity: 'Тяжесть (1–5)',
  expiresDays: 'Срок (дней, 0 — бессрочно)',
  issue: 'Применить',
  issuedStrike: 'Страйк выдан',
  issuedBan: 'Пользователь заблокирован',
  reasonRequired: 'Укажите причину',
  userIdRequired: 'Укажите корректный ID пользователя',
  // A8.06 revoke
  revoke: 'Снятие ограничения',
  revokeNote: 'Активные баны. Снятие восстанавливает доступ пользователю.',
  unban: 'Разблокировать',
  unbanConfirm: 'Снять бан с пользователя',
  unbanned: 'Бан снят',
  bannedAt: 'Заблокирован',
  // A8.07 change duration
  changeDuration: 'Изменение срока',
  changeDurationNote: 'Активные страйки. Продлить или сократить срок действия.',
  changeDurationAction: 'Изменить срок',
  newDurationDays: 'Новый срок (дней)',
  durationChanged: 'Срок обновлён',
  // A8.08 mass restriction (spam wave) — REAL users + bulk ban
  spamWaves: 'Массовое ограничение (спам)',
  spamWavesNote:
    'Выберите аккаунты (напр. волну спама) и заблокируйте их массово. Причина обязательна (аудит).',
  massRestrict: 'Ограничить выбранных',
  applied: 'Ограничение применено',
  massPartial: 'Ограничено частично',
  user: 'Пользователь',
  registered: 'Регистрация',
  lastSeen: 'Был(а)',
  // A8.09 templates
  templates: 'Шаблоны ограничений',
  templatesNote: 'Готовые сценарии наказания (действие + срок + текст причины).',
  name: 'Название',
  reasonText: 'Текст причины',
  // A8.12 appeal decisions
  decisions: 'Решения по апелляциям',
  decisionsNote: 'Журнал вынесенных решений (удовлетворено / отклонено).',
  kind: 'Тип',
  kindBan: 'Бан',
  kindStrike: 'Страйк',
  granted: 'Удовлетворено',
  rejected: 'Отклонено',
  decisionNote: 'Комментарий',
  open: 'Открыть',
  // A8.13 amnesty
  amnesty: 'Массовая амнистия',
  amnestyNote: 'Выберите заблокированных и снимите баны массово.',
  bulkUnban: 'Снять баны',
  selectRowsFirst: 'Сначала выберите строки',
  amnestyDone: 'Баны сняты',
  amnestyPartial: 'Частично снято',
  // A8.14 wrongful ban
  wrongful: 'Анализ ошибочных банов',
  wrongfulNote: 'Баны, отменённые апелляцией — сигнал о качестве модерации.',
  // A8.15 reasons catalog
  reasons: 'Каталог причин бана',
  reasonsNote: 'Стандартизированные коды причин с действием по умолчанию.',
  code: 'Код',
  category: 'Категория',
  label: 'Название',
  defaultAction: 'Действие по умолчанию',
  requiresEvidence: 'Нужны доказательства',
  // A8.16 device blocks
  deviceBlocks: 'Блокировки IP / устройств',
  deviceBlocksNote: 'Чёрный список IP, подсетей и отпечатков устройств.',
  blockKind: 'Тип',
  value: 'Значение',
  hits: 'Срабатываний',
  kindIp: 'IP',
  kindDevice: 'Устройство',
  kindSubnet: 'Подсеть',
  // A8.17 phone blocks
  phoneBlocks: 'Блокировки телефонов',
  phoneBlocksNote: 'Чёрный список номеров (ручной, авто, антифрод).',
  phone: 'Телефон',
  source: 'Источник',
  srcManual: 'Вручную',
  srcAuto: 'Авто',
  srcFraud: 'Антифрод',
  // A8.18 reregistration — merged blocked identifiers (IP/device + phone)
  rereg: 'Предотвращение перерегистрации',
  reregNote:
    'Заблокированные идентификаторы (IP/устройства + телефоны), не дающие забаненным зарегистрироваться заново. Удаление снимает барьер.',
  signal: 'Сигнал',
  match: 'Значение',
  sigDevice: 'Устройство',
  sigIp: 'IP',
  sigSubnet: 'Подсеть',
  sigPhone: 'Телефон',
  sigPrefix: 'Префикс',
  // A8.20 fairness
  fairness: 'Отчёт о справедливости',
  fairnessNote: 'Соотношение бан/разбан, доля отменённых решений.',
  bannedActive: 'Активных банов',
  appealsTotal: 'Всего апелляций',
  appealsDecided: 'Рассмотрено',
  overturnRate: 'Доля отмен',
  pending: 'В ожидании',
  banAppeals: 'Отменённых банов',
  strikeAppeals: 'Отменённых страйков',
} as const

export type RestrictionAction = AutoRuleRow['action']

/** Amal kodini RU yorlig'iga aylantirish (enum ustunlar uchun). */
export function actionLabel(a: string): string {
  switch (a) {
    case 'warn':
      return BAN_STR.actWarn
    case 'strike':
      return BAN_STR.actStrike
    case 'mute':
      return BAN_STR.actMute
    case 'ban':
      return BAN_STR.actBan
    default:
      return a
  }
}
